import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { AgentMappingContext, UserLoginContext } from "@/lib/authScope";
import type { Source } from "@/types";

type TempLogin = {
  merchantId: string;
  username: string;
  emailId?: string;
  phoneNumber?: string;
};

type AuthUser = {
  merchantId?: string;
  /** The BARE username from `/auth/me` — not the JWT subject (`name-MERCHANT`). */
  username?: string;
  /** Account id from `/auth/me`; the only reliable way to recognise our own row. */
  userId?: string;
  role?: string;
  source?: Source;
  staffType?: string;
  /** Commission agent flag from the JWT — unlocks the commission nav. */
  isAgent?: boolean;
  clientId?: number;
  clinicId?: number;
  employerId?: number;
  groupId?: number;
  divisionId?: number;
  locationId?: number;
  memberId?: number;
  staffTypeId?: number;
  /** Backend-resolved authorisation context (`/auth/me`) — see UserLoginContext. */
  features?: string[];
  writableFeatures?: string[];
  visibleMenuIds?: string[];
  manageableLevels?: string[];
  assignableUserLevels?: string[];
  commissionSections?: string[];
  agentMappings?: AgentMappingContext[];
  requirePasswordChange?: boolean;
};

type AuthState = {
  user?: AuthUser;
  sessionId?: string;
  tempLogin?: TempLogin;
};

const initialState: AuthState = { user: undefined, sessionId: undefined, tempLogin: undefined };

/**
 * The `/auth/me` fields the slice keeps. Everything here is a *rendering*
 * context resolved by the backend — the server re-checks all of it on every
 * request, so nothing in this slice grants access on its own.
 */
type LoginContextPayload = Pick<
  UserLoginContext,
  | "userId"
  | "username"
  | "features"
  | "writableFeatures"
  | "visibleMenuIds"
  | "manageableLevels"
  | "assignableUserLevels"
  | "commissionSections"
  | "agentMappings"
  | "requirePasswordChange"
  | "isAgent"
  | "scope"
>;

/**
 * A hierarchy id usable as a seat: present, numeric and positive. `0` and the
 * `-1` wildcard both mean "this level is not set" — the same rule the JWT path
 * already applies in `authScope`.
 */
function seatId(value: unknown): number | undefined {
  if (value == null) return undefined;
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : undefined;
}

const slice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setAuth(state, action: PayloadAction<{ user?: AuthUser; sessionId?: string }>) {
      if (action.payload.user) {
        // Merge rather than replace: the JWT-derived sync runs on every
        // session change and would otherwise wipe the richer `/auth/me`
        // context that landed after it.
        state.user = { ...state.user, ...action.payload.user };
      }
      if (action.payload.sessionId) {
        state.sessionId = action.payload.sessionId;
      }
    },
    /**
     * Merge the backend's login context over the JWT-derived seat. Undefined
     * fields are left alone — a partial update (clearing
     * `requirePasswordChange` after a reset, say) must not erase the rest.
     */
    setLoginContext(state, action: PayloadAction<LoginContextPayload>) {
      const { scope, ...rest } = action.payload;
      const next: AuthUser = { ...state.user };

      for (const [key, value] of Object.entries(rest)) {
        if (value !== undefined) {
          Object.assign(next, { [key]: value });
        }
      }

      // The JWT carries only the caller's own level, so this is the single
      // source for their full ancestor chain — which every scoped dropdown
      // needs before it can fetch a child list.
      //
      // Gap-fill only, and through `seatId`: `/auth/me` returns the raw staff
      // mapping columns, where an unfilled level can hold `0` or the `-1`
      // wildcard rather than null. Taken literally those become the seat, and
      // the dropdown at that level pins itself to a node that exists nowhere —
      // so the level renders EMPTY instead of unrestricted.
      if (scope) {
        for (const [key, value] of Object.entries(scope)) {
          const id = seatId(value);
          if (id != null && next[key as keyof AuthUser] == null) {
            Object.assign(next, { [key]: id });
          }
        }
      }

      state.user = next;
    },
    setTempLogin(state, action: PayloadAction<TempLogin>) {
      state.tempLogin = action.payload;
    },
    clearTempLogin(state) {
      state.tempLogin = undefined;
    },
    clearAuth(state) {
      state.user = undefined;
      state.sessionId = undefined;
      state.tempLogin = undefined;
    },
  },
});

export const { setAuth, setLoginContext, clearAuth, setTempLogin, clearTempLogin } = slice.actions;
export type { AuthUser };
export default slice.reducer;
